import React, { useEffect, useState } from 'react';
import { Box, HStack, Spinner, useToast } from '@chakra-ui/react';
import { Stack, Text } from '@chakra-ui/layout';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Footer, Heading, Input, Table } from '~/components';
import { FileUploader } from '~/components/FileInput';
import { ProjectInvoice } from '~/pages/Portal/Admin/ZeroCarbon/CreateZeroCarbonProject/ProjectInvoice';
import { createZeroCarbonProject } from '~/services/ZeroCarbon';
import { ZeroCarbonProject } from '~/types/zeroCarbon';
import { queryClient } from '~/utils/react-query';
import { formatCurrency } from '~/utils/formatCurrency';

const ProjectFormSchema = yup.object().shape({
  nome: yup
    .string()
    .required('Nome obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  quantidade_co2_contratado: yup
    .number()
    .required('Quantidade de CO2e obrigatória')
    .min(0.01, 'Quantidade deve ser maior que zero'),
  validadora: yup
    .string()
    .required('Validadora obrigatória')
    .max(255, 'Máximo de 255 caracteres'),
  anexo_apresentacao_nome: yup.string().required('Anexo obrigatório'),
  email: yup.string().required('Email obrigatório').email('Email inválido'),
});

export const CreateZeroCarbonProject: React.VFC = () => {
  const history = useHistory();
  const { register, setError, formState, clearErrors } = useForm({
    resolver: yupResolver(ProjectFormSchema),
  });
  const { errors } = formState;
  const toast = useToast();

  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [project, setProject] = useState<ZeroCarbonProject>({
    nome: '',
    quantidade_co2_contratado: 0,
    validadora: '',
    email: '',
    anexo_apresentacao: undefined,
    anexo_apresentacao_nome: '',
    notas_fiscais: [
      {
        numero: '',
        data_compra: undefined,
        valor: undefined,
        quantidade_co2_contratado: 0,
        anexo_nota_fiscal_nome: '',
        anexo_nota_fiscal: undefined,
      },
    ],
  } as ZeroCarbonProject);
  const [errosNotasFiscais, setErrosNotasFiscais] = useState<boolean>(false);

  const limparAnexoApresentacao = () => {
    setProject({
      ...project,
      anexo_apresentacao: undefined,
      anexo_apresentacao_nome: '',
    });
  };

  const possuiErros = () => {
    if (errosNotasFiscais) {
      toast({
        title: 'Preencha os campos obrigatórios da(s) nota(s) fiscal(is)',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return true;
    }
    try {
      ProjectFormSchema.validateSync(project, { abortEarly: false });
      clearErrors();
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        err.inner.forEach(e => {
          setError(e.path!, { message: e.message });
        });
        return true;
      }
    }
    return false;
  };

  const cadastrar = () => {
    if (possuiErros()) {
      return;
    }
    const formData = new FormData();
    formData.append('nome', project.nome);
    formData.append(
      'quantidade_co2_contratado',
      String(project.quantidade_co2_contratado),
    );
    formData.append('validadora', project.validadora);
    formData.append('email', project.email);
    formData.append('apresentacao', project.anexo_apresentacao!);
    project.notas_fiscais.forEach(nota => {
      formData.append('notas_fiscais', JSON.stringify(nota));
      if (nota.anexo_nota_fiscal) {
        formData.append('anexos', nota.anexo_nota_fiscal);
      }
    });
    setIsFetching(true);
    createZeroCarbonProject(formData)
      .then(() => {
        queryClient.invalidateQueries(['zero_carbon_projects']);
        history.goBack();
        toast({
          title: 'Projeto cadastrado com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .catch(err => {
        const errorsValidate = Object.entries(err.response.data);
        errorsValidate.forEach(_err => {
          if (_err[0] === 'notas_fiscais') {
            const errorsNotas = Object.entries(_err[1] as any);
            errorsNotas[0].forEach((e: any) => {
              const errorsNotasFiscais = Object.entries(e);
              errorsNotasFiscais.forEach((_e: any) => {
                if (_e[0] !== '0') {
                  toast({
                    title: 'Erro ao cadastrar projeto',
                    description: String(_e[1]),
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                  });
                }
              });
            });
          } else {
            toast({
              title: 'Erro ao cadastrar projeto',
              description: String(_err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          }
        });
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const calcularQuantidadeCo2Contratado = (notas: any) => {
    let quantidade = 0;
    notas.forEach((nota: any) => {
      quantidade += nota.quantidade_co2_contratado!;
    });
    setProject({
      ...project,
      quantidade_co2_contratado: Number(quantidade.toFixed(2)) || 0,
      notas_fiscais: notas,
    });
  };

  return (
    <Stack as="form">
      <Stack gridGap={4} px="8" py="4" minH="calc(100vh - 127px)">
        <HStack justifyContent="space-between" mb={4}>
          <Heading hasBackButton>Cadastrar projeto</Heading>
        </HStack>
        <Text size="md" fontWeight="bold">
          Dados do projeto
        </Text>
        <HStack alignItems="top">
          <Input
            value={project.nome}
            label="Nome do projeto"
            name="nome"
            isRequired
            type="text"
            error={errors.nome}
            onChange={e => {
              clearErrors('nome');
              setProject({ ...project, nome: e.target.value });
            }}
            isDisabled={isFetching}
          />
          <Input
            isReadOnly
            value={Number(project.quantidade_co2_contratado).toLocaleString(
              'pt-BR',
              {
                maximumFractionDigits: 0,
              },
            )}
            label="Qtde de CO2e adquirida"
            name="quantidade_co2_contratado"
            isRequired
            error={errors.quantidade_co2_contratado}
            isDisabled={isFetching}
          />
          <FileUploader
            isRequired
            fileName={project.anexo_apresentacao_nome}
            onChange={file => {
              clearErrors('anexo_apresentacao_nome');
              setProject({
                ...project,
                anexo_apresentacao: file,
                anexo_apresentacao_nome: file.name,
              });
            }}
            isDisabled={isFetching}
            clearFile={limparAnexoApresentacao}
            label="Anexar apresentação do projeto"
            showButton={false}
            helpText="Arquivo do tipo pdf. O limite total do arquivo é de 10MB."
            errors={errors.anexo_apresentacao_nome}
          />
        </HStack>
        <HStack alignItems="top">
          <Input
            value={project.validadora}
            label="Validadora"
            name="validadora"
            isRequired
            type="text"
            error={errors.validadora}
            onChange={e => {
              clearErrors('validadora');
              setProject({ ...project, validadora: e.target.value });
            }}
            isDisabled={isFetching}
          />
          <Input
            value={project.email}
            label="E-mail"
            name="email"
            isRequired
            type="text"
            error={errors.email}
            onChange={e => {
              clearErrors('email');
              setProject({ ...project, email: e.target.value });
            }}
            isDisabled={isFetching}
          />
        </HStack>
        <Text size="md" fontWeight="bold">
          Nota fiscal
        </Text>
        <ProjectInvoice
          isFetching={isFetching}
          notas_fiscais={project.notas_fiscais}
          onChangeList={(e, err) => {
            calcularQuantidadeCo2Contratado(e);
            setErrosNotasFiscais(err);
          }}
        />
      </Stack>
      <Footer>
        <Button variant="secondary" onClick={() => history.goBack()}>
          Cancelar
        </Button>
        <Button onClick={cadastrar} isDisabled={isFetching}>
          Cadastrar
          {isFetching && <Spinner size="md" ml="2" />}
        </Button>
      </Footer>
    </Stack>
  );
};
